<template>
  <div class="shipment-rate-shop-header">
    <div class="shipment-rate-shop-header-left flex flex-column flex-gap-1">
      <div class="flex flex-gap-1 align-items-center">
        <h2 :id="id" tabindex="-1" class="visually-hidden">
          {{ activeTab.header }}
        </h2>
        <RygenTabs
          :key="rygenTabsKey"
          v-model:active-tab-key="activeTabKey"
          :tabs="tabs"
        />

        <a
          v-if="routingGuideRule"
          :href="
            generateRoutingGuideLink(
              routingGuideRule,
              selectedDomain?.naturalId,
            )
          "
          class="routing-guide-link border-right-1 border-color-divider"
          :class="{ 'inactive line-through': !routingGuideRule.active }"
        >
          {{ routingGuideRule.name }}
        </a>
        <RygenChip
          v-if="mileageString && activeTab.showMileage"
          :label="mileageString"
        />
        <InfoIcon
          v-if="!rateShopLoading && mileageEstimate === '-'"
          class="text-warning"
          icon="pi pi-exclamation-triangle"
          :tooltip="t('rateShop.warnings.mileageRequestRejected')"
        />
        <LoadingIndicator :loading="loading" />
      </div>
    </div>

    <div
      class="shipment-rate-shop-header-right flex flex-gap-1 align-items-center"
    >
      <WaterfallTenderStatusView
        v-if="isOnWaterfallTenderTab && !!waterfallTender"
        :waterfall-tender="waterfallTender"
      />

      <ShipmentActionButton
        v-if="activeTab.actionButton === 'Shipment'"
        mode="secondary"
        :shipment="shipment"
        :carrier-settings="carrierSettings"
        :accepted-rate-quote="acceptedRateQuote"
        :cargo-insurance-status="cargoInsuranceStatus"
        :waterfall-tender="waterfallTender"
        rate-shop-only
        @command="emit('command', $event)"
      />
      <SpotMarketHeaderActionButton
        v-else-if="activeTab.actionButton === 'SpotMarket'"
        :can-request-spot-market-bid="isRateable"
        :can-cancel-all-spot-market-bids="canCancelAllSpotMarketBids"
        :loading="rateShopLoading || isBatchCancellingSpotMarkets"
        @cancel-all-spot-market-bids="emit('cancel-all-spot-market-bids')"
        @request-spot-market-bid="emit('request-spot-market-bid')"
      />
      <RateShopActionButton
        v-else-if="
          activeTab.actionButton === 'RateShop' &&
          (!waterfallTender ||
            waterfallTender?.status ===
              WaterfallTenderStatus.COMPLETED_WITHOUT_ACCEPTED_RATE_QUOTE)
        "
        :spot-quotable="isRateable"
        :updatable="isRateable"
        :brokerage-enabled="hasConfiguredBrokers"
        :waterfall-tender-enabled="isRateable"
        :loading="rateShopLoading"
        :can-request-spot-market-bid="isRateable"
        :cargo-insurance-status="cargoInsuranceStatus"
        :is-rate-selection-reason-required="isRateSelectionReasonRequired"
        :loadboardable="isRateable"
        :active-loadboards="activeLoadboards"
        show-cargo-insurance
        @add-spot-quote="emit('add-spot-quote')"
        @cargo-insurance-cancel="
          emit('command', { action: ShipmentAction.CARGO_INSURANCE_CANCEL })
        "
        @cargo-insurance-purchase="
          emit('command', { action: ShipmentAction.CARGO_INSURANCE_PURCHASE })
        "
        @post-to-loadboard="emit('post-to-loadboard')"
        @remove-loadboard="emit('remove-loadboard', $event)"
        @request-spot-market-bid="emit('request-spot-market-bid')"
        @send-to-brokerage="emit('send-to-brokerage')"
        @update-rates="emit('update-rates')"
        @waterfall-tender="emit('waterfall-tender')"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { Component, PropType } from 'vue';
import { computed, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useRygenTabs } from '@rygen/rygen-ui/composables';
import {
  ActionCommand,
  CarrierSetting,
  Loadable,
  Tab,
  UUID,
} from '@rygen/rygen-ui/model';

import {
  useLowestCostReasonCode,
  useRateShop,
  useRoutingGuide,
} from '@/composables';
import {
  CargoInsuranceStatus,
  LoadboardSource,
  RateShop,
  type ServiceTypeFilters,
  Shipment,
  ShipmentAction,
  ShipmentStatus,
  WaterfallTender,
  WaterfallTenderStatus,
} from '@/model';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { generateRoutingGuideLink } from '@/util';

import RateShopActionButton from '@/components/rateShop/RateShopActionButton.vue';
import SpotMarketHeaderActionButton from '@/components/rateShop/SpotMarketHeaderActionButton.vue';
import WaterfallTenderStatusView from '@/components/rateShop/WaterfallTenderStatus.vue';
import ShipmentActionButton from '@/components/shipment/ShipmentActionButton.vue';
import InfoIcon from '@rygen/rygen-ui/components/ui/InfoIcon.vue';
import LoadingIndicator from '@rygen/rygen-ui/components/ui/LoadingIndicator.vue';
import RygenChip from '@rygen/rygen-ui/components/ui/RygenChip.vue';
import RygenTabs from '@rygen/rygen-ui/components/ui/RygenTabs.vue';

export type TabKey =
  | 'RATES'
  | 'SPOT_MARKETS'
  | 'ACCEPTED_RATE_QUOTE'
  | 'OTHER_RATES'
  | 'WATERFALL_TENDER'
  | 'LOADBOARDS';

export interface ShipmentRateShopTab extends Tab<TabKey> {
  showMileage: boolean;
  actionButton: 'Shipment' | 'RateShop' | 'SpotMarket' | 'NONE';
  component: Component;
}

const activeTabKey = defineModel('activeTabKey', {
  type: String as PropType<TabKey>,
  default: () => 'RATES',
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  shipment: {
    type: Shipment,
    default: () => new Shipment(),
  },
  rateShop: {
    type: Object as PropType<Loadable<RateShop>>,
    default: () => ({ loading: false }),
  },
  tabs: {
    type: Array as PropType<ShipmentRateShopTab[]>,
    default: () => [],
  },
  tabStateKey: {
    type: String as PropType<UUID>,
    required: true,
  },
  carrierSettings: {
    type: CarrierSetting,
    default: () => new CarrierSetting(),
  },
  cargoInsuranceStatus: {
    type: String as PropType<CargoInsuranceStatus | undefined>,
    default: () => undefined,
  },
  estimatedMileage: {
    type: Number,
    default: () => 0,
  },
  waterfallTender: {
    type: Object as PropType<WaterfallTender | undefined>,
    default: () => undefined,
  },
  serviceTypeFilters: {
    type: Object as PropType<ServiceTypeFilters>,
    default: () => ({}),
  },
  hasConfiguredBrokers: {
    type: Boolean,
    default: () => false,
  },
  rateShopLoading: {
    type: Boolean,
    default: () => false,
  },
  loading: {
    type: Boolean,
    default: () => false,
  },
  activeLoadboards: {
    type: Array<LoadboardSource>,
    default: () => [],
  },
  canCancelAllSpotMarketBids: {
    type: Boolean,
    default: () => false,
  },
  isBatchCancellingSpotMarkets: {
    type: Boolean,
    default: () => false,
  },
});
const {
  id,
  shipment,
  rateShop,
  tabs,
  tabStateKey,
  carrierSettings,
  estimatedMileage,
  waterfallTender,
  serviceTypeFilters,
  hasConfiguredBrokers,
  rateShopLoading,
  loading,
  activeLoadboards,
  canCancelAllSpotMarketBids,
  isBatchCancellingSpotMarkets,
} = toRefs(props);
const emit = defineEmits<{
  (event: 'add-spot-quote'): void;
  (event: 'cancel-all-spot-market-bids'): void;
  (event: 'command', payload: ActionCommand<ShipmentAction>): void;
  (event: 'remove-loadboard', loadboard: LoadboardSource): void;
  (event: 'post-to-loadboard'): void;
  (event: 'request-spot-market-bid'): void;
  (event: 'send-to-brokerage'): void;
  (event: 'update-rates'): void;
  (event: 'waterfall-tender'): void;
}>();

const { t } = useI18n();
const { selectedDomain } = useCurrentUserStore();
const { isRateSelectionReasonRequired } = useLowestCostReasonCode();
const { acceptedRateQuote } = useRateShop(rateShop, {
  acceptedRateQuoteId: toRef(() => shipment.value.acceptedRateQuoteId),
});
const { routingGuideRule } = useRoutingGuide(rateShop, serviceTypeFilters);
const { activeTab, visibleTabs } = useRygenTabs(tabStateKey);

/** Remount when visible tabs change so PrimeVue scrollable TabList re-measures. */
const rygenTabsKey = computed(() =>
  visibleTabs.value.map((tab) => tab.key).join('|'),
);

const isRateable = computed(() => {
  return shipment.value.status === ShipmentStatus.PLANNING;
});

const mileageEstimate = computed(() => {
  let mileage;
  if (acceptedRateQuote.value?.totalMileage !== undefined) {
    return acceptedRateQuote.value.totalMileage;
  } else if (estimatedMileage.value !== undefined) {
    mileage = estimatedMileage.value;
  } else {
    mileage = rateShop.value.data?.rateQuotes
      ?.map((x) => x.totalMileage)
      ?.find((x) => !!x);
  }
  return mileage ? Math.round(mileage) : '-';
});

const mileageString = computed(() => {
  const rateTypeHasQuotedMileage = !(
    acceptedRateQuote.value?.source === 'LANE_QUOTE' ||
    acceptedRateQuote.value?.source === 'SPOT_QUOTE'
  );

  if (
    acceptedRateQuote.value?.totalMileage !== undefined &&
    rateTypeHasQuotedMileage
  ) {
    return t('rateShop.quotedMileageCount', {
      param: t('rateShop.mileage', {
        count: mileageEstimate.value,
      }),
    });
  }
  return t('rateShop.estimatedMileageCount', {
    param: t('rateShop.mileage', {
      count: mileageEstimate.value,
    }),
  });
});

const isOnWaterfallTenderTab = computed(() => {
  return activeTabKey.value === 'WATERFALL_TENDER';
});
</script>

<style>
.shipment-rate-shop-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--gap-1);
  width: 100%;
}
</style>
